/**
 * The recommendation, and the gate in front of the barcode.
 *
 * ALGORITHM.md §12 is explicit: never render a barcode for an unverified activation state. That is
 * enforced here structurally — the barcode component is only reachable from the branch where the
 * server returned `verified: true` and a card number, and the failure branch shows the fallback
 * card as digits and reasons instead.
 */
import { useState } from "react";
import { euros, isoDay, maskedCard, mechanicLabel, percent, quantity, REGIME_COPY, TIER_COPY } from "../format.ts";
import { navigate } from "../hooks.ts";
import { useModel } from "../model.tsx";
import { Ean13 } from "../components/Ean13.tsx";
import { CardFace } from "../components/CardFace.tsx";
import { Icon } from "../components/icons.tsx";
import { Badge, Empty, Flags, Meter, Note, Slots, Spinner, Stat } from "../components/ui.tsx";
import type { Assignment, Candidate, Offer } from "../../../server/types.ts";

function contribution(assignments: Assignment[], hqId: number): { cents: number; units: number } {
	let cents = 0;
	let units = 0;
	for (const assignment of assignments) {
		if (assignment.offerHqId !== hqId) continue;
		cents += assignment.discountCents;
		units += assignment.units;
	}
	return { cents, units };
}

function OfferRow({ offer, assignments, active }: { offer: Offer; assignments: Assignment[]; active: boolean }) {
	const { cents, units } = contribution(assignments, offer.hqId);
	return (
		<li className={cents > 0 ? "offer" : "offer offer-idle"}>
			<span className="offer-head">
				<span className="offer-mech">{mechanicLabel(offer.mechanic)}</span>
				<span className="offer-value">{cents > 0 ? `\u2212${euros(cents)}` : "no value here"}</span>
			</span>
			<span className="offer-title">{offer.title}</span>
			{offer.subtitle !== null && <span className="offer-sub">{offer.subtitle}</span>}
			<span className="offer-meta">
				{active && <Badge tone="money">already active</Badge>}
				{units > 0 && (
					<span>
						{quantity(units)} {units === 1 ? "item" : "items"}
					</span>
				)}
				<span>
					{offer.productIds.length}/{offer.productCount} products
				</span>
				{!offer.coverageComplete && <Badge tone="warn">coverage truncated</Badge>}
				{offer.mechanic.kind === "UNSUPPORTED" && <Badge tone="warn">not modelled</Badge>}
				{!offer.stackable && <Badge>no stacking</Badge>}
				{offer.maxUnits !== null && <span>max {offer.maxUnits}</span>}
				<span>
					till {isoDay(offer.periodEnd)}
				</span>
			</span>
			{offer.exceptionRule !== null && (
				<span className="offer-exception">
					<Icon name="alert" /> {offer.exceptionRule}
				</span>
			)}
		</li>
	);
}

function CandidateBody({ candidate }: { candidate: Candidate }) {
	return (
		<>
			<span className="cand-head">
				<span className="cand-card">{maskedCard(candidate.bonusCard)}</span>
				<Badge tone={candidate.tier <= 1 ? "money" : candidate.tier === 2 ? "accent" : "warn"}>
					tier {candidate.tier}
				</Badge>
				{candidate.role !== null && <Badge>{candidate.role.toLowerCase()}</Badge>}
				<Badge>{candidate.lifecycle.toLowerCase()}</Badge>
			</span>
			<span className="cand-email">{candidate.email}</span>
			<span className="cand-tier">{TIER_COPY[candidate.tier] ?? ""}</span>
			<div className="stats">
				<Stat label="Discount" value={euros(candidate.plan.discountCents)} />
				<Stat label="Score" value={euros(candidate.scoreCents)} hint="after slot cost and similarity" />
				<Stat label="Slot cost" value={euros(candidate.lambdaCents)} hint="value the slots give up" />
				<Stat label="Similarity" value={percent(candidate.similarity)} />
				<Stat
					label="Overlap"
					value={`${candidate.overlap.toFixed(1)} / ${candidate.overlapRequired.toFixed(1)}`}
					hint="informative products matched"
				/>
				<Stat label="Dilution" value={percent(candidate.dilution)} hint="basket over profile mass" />
			</div>
			<span className="cand-slots">
				<Slots used={candidate.plan.slotsUsed} free={candidate.plan.slotsFree} />
				<span>
					{candidate.plan.activate.length} to activate, {candidate.plan.alreadyActive.length} already on
				</span>
			</span>
		</>
	);
}


export function Result() {
	const model = useModel();
	const { basket, rec, commitResult } = model;
	const [full, setFull] = useState(false);

	if (basket === null || basket.lines.length === 0) {
		return (
			<>
				<main className="screen">
					<Empty title="Nothing to solve">
						There is no basket yet. Scan a few items and the recommendation appears here.
					</Empty>
				</main>
				<div className="actionbar">
					<button type="button" className="btn btn-primary btn-lg" onClick={() => navigate("scan")}>
						<Icon name="scan" /> Start scanning
					</button>
				</div>
			</>
		);
	}

	const winner = rec?.winner ?? null;
	const runnerUp = rec?.runnerUp ?? null;
	const activate = winner?.plan.activate ?? [];
	const rejected = rec?.candidates.filter((candidate) => candidate.rejected !== null) ?? [];
	const verifiedCard = commitResult !== null && commitResult.verified ? commitResult.bonusCard : null;
	const committedTo =
		commitResult === null ? null : (rec?.candidates.find((c) => c.accountId === commitResult.accountId) ?? null);

	if (full && verifiedCard !== null) return <CardFace card={verifiedCard} onClose={() => setFull(false)} />;

	return (
		<>
			<main className="screen result">
				{model.recError !== null && (
					<>
						<Note tone="danger">{model.recError}</Note>
						<button type="button" className="btn btn-ghost" onClick={model.reloadRec}>
							<Icon name="refresh" /> Solve again
						</button>
					</>
				)}

				{rec === null ? (
					model.recError === null && (
						<div className="loading">
							<Spinner label="Solving every card against this basket" />
						</div>
					)
				) : (
					<>
						{commitResult !== null && (
							<section className={commitResult.verified ? "panel committed" : "panel committed committed-failed"}>
								{verifiedCard !== null ? (
									<>
										<h1 className="panel-title">
											<Icon name="check" /> Activated and verified
										</h1>
										<p className="committed-lede">
											Every offer read back as active on this card. Scan this at the till, before the cashier
											totals up.
										</p>
										<button type="button" className="barcode-wrap" onClick={() => setFull(true)}>
											<Ean13 value={verifiedCard} />
											<span className="barcode-digits">{verifiedCard}</span>
											<span className="barcode-hint">Tap for full screen</span>
										</button>
										<div className="stats">
											<Stat label="Expected discount" value={euros(commitResult.expectedDiscountCents)} />
											<Stat label="Offers activated" value={String(commitResult.activated.length)} />
											<Stat label="Trip" value={`#${commitResult.tripId}`} />
										</div>
									</>
								) : (
									<>
										<h1 className="panel-title">
											<Icon name="alert" /> Not verified, so no barcode
										</h1>
										<p className="committed-lede">
											The activation did not read back clean, and a barcode for an unverified card would
											underdeliver at the till. Use the card below, or retry.
										</p>
										{commitResult.failed.length > 0 && (
											<ul className="failed">
												{commitResult.failed.map((failure) => (
													<li key={failure.hqId}>
														<strong>{failure.title}</strong>
														<span>{failure.reason}</span>
													</li>
												))}
											</ul>
										)}
										{commitResult.activated.length > 0 && (
											<p className="committed-note">
												{commitResult.activated.length} offer
												{commitResult.activated.length === 1 ? "" : "s"} did activate; a slot spent is spent for
												the week, there is no way to hand it back.
											</p>
										)}
										<div className="stats">
											<Stat
												label="Card used"
												value={maskedCard(committedTo?.bonusCard ?? null)}
												hint={`account ${commitResult.accountId}`}
											/>
											<Stat label="Trip" value={`#${commitResult.tripId}`} />
											{commitResult.fellBackTo !== null && (
												<Stat label="Fell back to" value={`account ${commitResult.fellBackTo}`} />
											)}
										</div>
									</>
								)}
								{commitResult.flags.length > 0 && <Flags flags={commitResult.flags} />}
							</section>
						)}

						{model.commitError !== null && <Note tone="danger">{model.commitError}</Note>}

						{winner === null ? (
							<Empty title="No card qualifies">
								Every candidate was rejected or the pool is empty for this trip. The reasons are listed below;
								usually it means no account has a fresh box, or the only cards with offers belong to another
								category and the crossing test blocked them.
							</Empty>
						) : (
							<>
								<section className="hero">
									<span className="hero-label">
										{commitResult === null ? "Expected discount" : "Discount on this trip"}
									</span>
									<span className="hero-figure">
										<i>&minus;</i>
										{euros(commitResult?.expectedDiscountCents ?? winner.plan.discountCents)}
									</span>
									<span className="hero-sub">
										on {euros(rec.totalCents)}
										{winner.plan.discountCents > 0 && rec.totalCents > 0
											? ` \u00b7 ${percent(winner.plan.discountCents / rec.totalCents)} off`
											: ""}
										{!model.recFresh && commitResult === null ? " \u00b7 basket has changed" : ""}
									</span>
									<span className="hero-badges">
										<Badge tone={rec.regime === "HARVEST" ? "money" : "accent"}>
											{REGIME_COPY[rec.regime].label}
										</Badge>
										{rec.seeding && <Badge tone="warn">seeding a new profile</Badge>}
										<Badge>{rec.categoryName ?? "no category"}</Badge>
										<Badge>week of {isoDay(rec.weekStart)}</Badge>
									</span>
								</section>

								<section className="panel winner">
									<h2 className="panel-title">Pay with this card</h2>
									<CandidateBody candidate={winner} />
								</section>

								<section className="panel">
									<h2 className="panel-title">
										{activate.length === 0
											? "Nothing to activate"
											: `Activate ${activate.length} offer${activate.length === 1 ? "" : "s"} first`}
									</h2>
									{activate.length === 0 ? (
										<p className="panel-lede">
											{winner.plan.alreadyActive.length > 0
												? "The offers that pay on this basket are already active on the card, so there is nothing to spend a slot on."
												: "No offer on this card touches this basket. Committing still records the trip, which is what teaches the profile."}
										</p>
									) : (
										<p className="panel-lede">
											Activation is one-way: AH has no deactivate, so a slot spent is gone for the week.
										</p>
									)}
									{activate.length > 0 && (
										<ul className="offers">
											{activate.map((offer) => (
												<OfferRow key={offer.hqId} offer={offer} assignments={winner.plan.assignments} active={false} />
											))}
										</ul>
									)}
									{winner.plan.alreadyActive.length > 0 && (
										<>
											<h3 className="panel-sub">Already active, no slot cost</h3>
											<ul className="offers">
												{winner.plan.alreadyActive.map((offer) => (
													<OfferRow key={offer.hqId} offer={offer} assignments={winner.plan.assignments} active />
												))}
											</ul>
										</>
									)}
								</section>

								<section className="panel">
									<h2 className="panel-title">Why this card</h2>
									<ol className="reasons">
										{rec.reason.map((line) => (
											<li key={line}>{line}</li>
										))}
										<li>{REGIME_COPY[rec.regime].detail}</li>
									</ol>
									<div className="stats">
										<Stat label="Best over tiers 0-2" value={euros(rec.dMaxCents)} hint="D_max" />
										<Stat label="Worthwhile floor" value={euros(rec.dFloorCents)} hint="D_floor" />
										<Stat label="Solved in" value={`${rec.computedMs} ms`} />
										<Stat label="Candidates" value={String(rec.candidates.length)} />
									</div>
								</section>

								<section className="panel">
									<h2 className="panel-title">How much to trust this</h2>
									<Flags flags={rec.flags} />
								</section>

								{runnerUp !== null && (
									<section className="panel runnerup">
										<h2 className="panel-title">Fallback card</h2>
										<p className="panel-lede">
											{euros(winner.plan.discountCents - runnerUp.plan.discountCents)} worse than the winner. Use
											this if activation on the winner fails.
										</p>
										<CandidateBody candidate={runnerUp} />
										<button
											type="button"
											className="btn btn-ghost"
											disabled={model.committing || !model.recFresh || basket.committedTripId !== null}
											onClick={() => void model.commit(runnerUp.accountId)}
										>
											Commit to this card instead
										</button>
									</section>
								)}

								{rejected.length > 0 && (
									<details className="panel">
										<summary>Why not the other {rejected.length} cards</summary>
										<ul className="rejects">
											{rejected.map((candidate) => (
												<li key={candidate.accountId}>
													<span className="reject-card">{maskedCard(candidate.bonusCard)}</span>
													<span className="reject-value">{euros(candidate.plan.discountCents)}</span>
													<span className="reject-why">{candidate.rejected}</span>
													<Meter value={candidate.similarity} tone="neutral" />
												</li>
											))}
										</ul>
									</details>
								)}
							</>
						)}
					</>
				)}
			</main>

			<div className="actionbar">
				{verifiedCard !== null ? (
					<>
						<button type="button" className="btn btn-ghost btn-lg" onClick={() => void model.startNewBasket()}>
							New basket
						</button>
						<button type="button" className="btn btn-primary btn-lg" onClick={() => setFull(true)}>
							<Icon name="card" /> Show barcode
						</button>
					</>
				) : basket.committedTripId !== null && commitResult === null ? (
					<>
						<span className="ab-note">
							Committed as trip #{basket.committedTripId}. A barcode is never re-rendered from memory, so open the
							trip in History to see what it did.
						</span>
						<button type="button" className="btn btn-primary btn-lg" onClick={() => void model.startNewBasket()}>
							New basket
						</button>
					</>
				) : (
					<>
						<div className="actionbar-figures">
							<span className="ab-total">
								<small>Basket</small>
								{euros(basket.totalCents)}
							</span>
						</div>
						<button
							type="button"
							className="btn btn-primary btn-lg"
							disabled={winner === null || model.committing || !model.recFresh}
							onClick={() => void model.commit()}
						>
							{model.committing ? (
								<Spinner label="Activating" />
							) : activate.length === 0 ? (
								"Confirm and show card"
							) : (
								`Activate ${activate.length} \u00b7 show card`
							)}
						</button>
					</>
				)}
			</div>
		</>
	);
}
